import { LitElement, html, nothing, svg, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { Virtualizer, observeElementOffset, observeElementRect, elementScroll, type VirtualItem } from '@tanstack/virtual-core';
import { ref } from 'lit/directives/ref.js';
import { t } from '@vitesse/base-ui/i18n';
import type { StreamFrame } from '../../core/bridge/types';
import { parseSearchInput, matchesFilters, type SearchCommand, type ParsedSearch } from '@vitesse/base-ui/search';
import { TreeSearch } from '@vitesse/base-ui/search';
import { ColumnResizer } from '@vitesse/base-ui/grid';
import { fmtDate, fmtTime } from '@vitesse/base-ui/format';

/** One display log line — the uniform shape <vitesse-log-tree> renders.
 *  Deliberately tiny: a `severity` (info | warning | error) drives the colour
 *  badge; a `title` is the collapsed one-liner; an optional `body` is the
 *  expanded detail. Everything else is natural language in the title/body. */
export interface LogLine {
  tsMs: number;
  severity: string; // "info" | "warn"/"warning" | "error" (anything else → info)
  title: string;
  body?: string;
  /** An explicit Status-column chip. Severity only badges warnings and errors
   *  (routine info stays quiet), which is right for fault logs and leaves the
   *  column EMPTY for feeds whose lines are all routine — an empty column is a
   *  column that teaches nothing. A feed whose lines have a meaningful kind
   *  (created/edited, started/stopped) names it here and every line gets a
   *  chip. */
  status?: { label: string; color: 'blue' | 'gray' | 'green' | 'yellow' | 'red' };
}

/*
 * <vitesse-log-tree> — the ONE reusable log view, app-wide (controllers, models,
 * future entities).
 *
 * It renders inside the symbol tree's `.vg-wrapper` and REUSES the symbol grid's
 * chrome VERBATIM (toolbar, search, expand/collapse + bulk, header, checkbox,
 * chevron, empty-state, resize-handle + `.vg-resize-highlight`) — ONE source of
 * truth in symbolGrid.scss, so the two views can't drift. Only what genuinely
 * differs lives in log-tree.scss: the log's column template + the row (one
 * stretched grid row per line so the column walls run the full height even when
 * the body is expanded) + the expandable body.
 *
 * VIRTUALIZED: only the visible window of rows is in the DOM (@tanstack/virtual-
 * core against the page scroller, like the symbol grid), so DOM is O(viewport)
 * regardless of backlog — `lines` can hold the backend's whole window and the
 * page stays light. The full `shown` list is still iterated for filter/count/
 * select-all, never rendered. See renderBody()/the virtualizer setup below.
 *
 * Behaviour: collapsed-by-default; dedup of re-delivered lines; append-only (old
 * lines are NOT dropped on each new one — a generous cap bounds the data; the DOM
 * is bounded by virtualization regardless); land at the TOP (oldest first) on
 * open — no autoscroll jump — and only stick-to-bottom for live lines once the
 * user has scrolled to the bottom ("following"); resizable Time/Status columns
 * with the blue wall highlight; and `/time` `/status` `/message` search commands
 * (compoundable with & | !).
 */
// Retention is the BACKEND's job (events_log is a disk-backed, per-subject
// circular buffer — last ~1000 logs per controller). The frontend never deletes
// what you're reading: while you're scrolled up in history it ONLY appends; a
// generous hard cap bounds memory if you sit in history for a very long time.
// When you're following the live tail (at the bottom) it mirrors the backend's
// ~1000 circular bound — and you can't see that eviction because you're pinned
// to the bottom anyway.
const SOFT_CAP = 1000; // mirrors the backend per-controller bound, applied only while following
const HARD_CAP = 5000; // memory backstop, applied only while reading history
// Coalesce renders to a fixed cadence (NOT per-rAF). A spammy producer (e.g. a
// model self-transitioning on every value change) can stream logs far faster than
// the eye reads them; flushing per-rAF re-runs filter/dedup/sort + re-renders the
// list ~60×/s and pegs the main thread. A wall-clock interval caps that to ~5
// renders/s regardless of incoming rate — the tail still feels live, the page
// never janks. A burst (or the replay snapshot) coalesces into ONE render.
const FLUSH_INTERVAL_MS = 200;
// Virtualization — render ONLY the visible window of rows (≈ viewport + overscan),
// never the whole list. This is what makes the log view O(viewport) DOM instead of
// O(N): a 1000-row backlog of these heavy rows (checkbox + 4 cells + SVG + spans ≈
// 16 nodes each, with native style/layout backing) was ~hundreds of MB resident;
// windowed, it's a few dozen rows regardless of backlog. Same @tanstack/virtual-core
// the SymbolGrid uses, virtualized against the page scroller (.main-content) with a
// scrollMargin — so the existing page-scroll model is unchanged. Rows are variable
// height (an expanded body is taller), so the virtualizer MEASURES each rendered row
// (estimate is just the collapsed height).
const ROW_HEIGHT_EST = 35; // collapsed row ≈ var(--vg-row-height) 34 + 1px border
const OVERSCAN = 12; // rows kept rendered beyond the viewport each side

type Sev = 'info' | 'warn' | 'error';

// Icons — the EXACT symbol-tree glyphs + sizes (nothing a second, larger copy).
const EXPAND_ICON = svg`<path d="m7 15 5 5 5-5" /><path d="m7 9 5-5 5 5" />`; // chevrons-up-down
const COLLAPSE_ICON = svg`<path d="m7 20 5-5 5 5" /><path d="m7 4 5 5 5-5" />`; // chevrons-down-up
const wrapSvg = (size: number, paths: unknown) => svg`<svg
  width="${size}" height="${size}" viewBox="0 0 24 24" fill="none"
  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
>${paths}</svg>`;
const SEARCH_ICON = html`<svg
  class="vg-search-icon"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <circle cx="11" cy="11" r="8" />
  <path d="m21 21-4.35-4.35" />
</svg>`;
const CLEAR_ICON = html`<svg
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <circle cx="12" cy="12" r="10" />
  <path d="m15 9-6 6" />
  <path d="m9 9 6 6" />
</svg>`;
const COPY_ICON = html`<svg
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <rect x="9" y="9" width="11" height="11" rx="2" />
  <path d="M5 15V5a2 2 0 0 1 2-2h10" />
</svg>`;
const CHEVRON_SVG = html`<svg
  class="vg-chevron"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="m7 15 5 5 5-5" />
</svg>`;

// Tight defaults: Time fits "Jun 13 13:56:29", Status fits the centred "Warning".
const DEFAULT_TIME_W = 128;
const DEFAULT_STATUS_W = 84;
const MIN_COL_W = 56;

// ── Search: command spec + matchers driving the SHARED tree-search engine — the
// same parser, syntax-highlight, and Discord-style autocomplete the symbol tree
// uses. The log's columns become its commands: `/time`, `/status`, `/message`,
// compoundable with & | != exactly like the grid. ──────────────────────────
interface LogCtx {
  fmtTime: (tsMs: number) => string;
}

function sevOf(s: string): 'info' | 'warn' | 'error' {
  if (s === 'error') return 'error';
  if (s === 'warn' || s === 'warning') return 'warn';
  return 'info';
}
function sevLabelOf(s: string): string {
  const v = sevOf(s);
  return v === 'error' ? 'error' : v === 'warn' ? 'warning' : 'info';
}

const LOG_COMMANDS: SearchCommand<LogLine, LogCtx>[] = [
  {
    name: 'message',
    descKey: 'Filter by message text',
    valueType: 'string',
    placeholder: '=link',
    match: (l, v) => !v || `${l.title} ${l.body ?? ''}`.toLowerCase().includes(v.toLowerCase()),
    suggestValue: (l) => l.title,
  },
  {
    name: 'status',
    descKey: 'Filter by severity',
    valueType: 'enum',
    enumValues: ['info', 'warning', 'error'],
    placeholder: '=warning',
    match: (l, v) => !v || sevLabelOf(l.severity).includes(v.toLowerCase()),
    suggestValue: (l) => sevLabelOf(l.severity),
  },
  {
    name: 'time',
    descKey: 'Filter by timestamp',
    valueType: 'string',
    placeholder: '=13:5',
    match: (l, v, ctx) => !v || ctx.fmtTime(l.tsMs).toLowerCase().includes(v.toLowerCase()),
  },
];

// Free-text match across every field (the default when no /command is typed).
function logTextMatch(l: LogLine, q: string, ctx: LogCtx): boolean {
  const hay = `${ctx.fmtTime(l.tsMs)} ${sevLabelOf(l.severity)} ${l.title} ${l.body ?? ''}`.toLowerCase();
  return hay.includes(q.toLowerCase());
}

@customElement('vitesse-log-tree')
export class LogTree extends LitElement {
  /** Re-subscribe whenever this changes (the entity id). Empty → no stream. */
  @property() subscriptionKey = '';
  /** Opens the stream; returns the transferred MessagePort. */
  @property({ attribute: false }) connect?: () => Promise<MessagePort>;
  /** Maps one streamed item to a display line. */
  @property({ attribute: false }) toLine: (item: unknown) => LogLine = (i) => i as LogLine;
  /** Empty-state title (short) + description — mirrors the grid's "No symbols yet". */
  @property() emptyTitle = 'No logs yet';
  @property() emptyDesc = 'Logs will appear here as activity is reported.';

  @state() private lines: LogLine[] = [];
  @state() private query = '';
  @state() private expandAllMode = false; // collapsed by default
  @state() private toggled = new Set<string>(); // per-row exceptions to expandAllMode
  @state() private selected = new Set<string>(); // keys of manually-selected rows
  @state() private selectAllMode = false; // "select all" = a MODE: every row incl. ones that arrive later
  @state() private colTime = DEFAULT_TIME_W;
  @state() private colStatus = DEFAULT_STATUS_W;

  private port?: MessagePort;
  private wiredFor = '';
  private pending: LogLine[] = [];
  private flushScheduled = false;
  private flushTimer = 0;
  private following = false; // true once the user scrolls to the bottom → live lines stick
  private scrollAfterUpdate = false; // set by flush() when we should stick to bottom
  private treeSearch?: TreeSearch<LogLine, LogCtx>; // shared search controller (input owns itself)
  private resizer?: ColumnResizer; // shared column-resize chrome (blue wall + drag)
  // Virtualization state. `shown` is the FULL filtered list (what select-all/copy/
  // search/counts operate on); only the virtualizer's window is actually rendered.
  private virtualizer?: Virtualizer<HTMLElement, Element>;
  private vCleanup?: () => void;
  private scrollParent: HTMLElement | null = null;
  private shown: LogLine[] = [];
  // Loop guard: the virtualizer's onChange fires on scroll/resize/measure; we
  // requestUpdate when the rendered index window moves OR when a measurement
  // changed the total layout size (a row turned out taller than the estimate —
  // e.g. an expanded body). The size check is essential: without it, a measured
  // row never pushes its successors down → overlapping rows. It still can't spin
  // because the size CONVERGES (once rows measure stable, total stops changing).
  private renderedRange = { start: -1, end: -1 };
  private renderedTotal = -1;

  // ⌘/ (same shortcut as the symbol grid) focuses the search.
  private readonly onKeydown = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === '/') {
      const input = this.querySelector<HTMLInputElement>('.vg-search-input');
      if (input) {
        e.preventDefault();
        input.focus();
      }
    }
  };
  // Track whether the user is at the bottom (following the live tail). Initialised
  // false so opening the log lands at the TOP (oldest) with no autoscroll.
  private readonly onScroll = () => {
    this.following = this.isNearPageBottom();
  };

  protected createRenderRoot(): HTMLElement {
    return this; // light DOM → the shared `.vg-*` agent-styles apply
  }

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onKeydown);
    // The scroll listener attaches to the real scroller (.main-content) in
    // firstUpdated, once we're in the DOM and can walk up to find it.
  }

  /** The page scroller this log lives in (.main-content / nearest overflow:auto). */
  private scrollEl(): HTMLElement {
    if (this.scrollParent?.isConnected) return this.scrollParent;
    let el: HTMLElement | null = this;
    while (el) {
      if (el.classList?.contains('main-content')) {
        this.scrollParent = el;
        return el;
      }
      const oy = getComputedStyle(el).overflowY;
      if (oy === 'auto' || oy === 'scroll') {
        this.scrollParent = el;
        return el;
      }
      el = el.parentElement;
    }
    this.scrollParent = (document.scrollingElement as HTMLElement) || document.documentElement;
    return this.scrollParent;
  }

  protected willUpdate(changed: PropertyValues): void {
    super.willUpdate?.(changed);
    const key = this.subscriptionKey;
    if (key && this.connect && this.wiredFor !== key) {
      this.wiredFor = key;
      this.teardown();
      void this.connect().then((port) => {
        if (this.subscriptionKey !== key || !this.isConnected) {
          port.postMessage({ kind: 'close' });
          port.close();
          return;
        }
        this.port = port;
        port.onmessage = (e: MessageEvent<StreamFrame<unknown>>) => {
          if (e.data.kind !== 'delta') return;
          this.pending.push(this.toLine(e.data.item));
          this.scheduleFlush();
        };
        port.start();
      });
    }
  }

  protected firstUpdated(): void {
    // Virtualizer + scroll listener first, so they're wired even if the search
    // chrome below isn't present for some reason.
    this.setupVirtualizer();
    this.scrollEl().addEventListener('scroll', this.onScroll, { passive: true });

    // Attach the SHARED tree-search controller to the (stable) search-wrap. It
    // owns the input + highlight overlay + autocomplete imperatively, so the
    // input is NOT a Lit-bound value — `query` is driven from onChange and used
    // only for filtering, leaving the controller free to manage the field.
    const wrap = this.querySelector<HTMLElement>('.vg-search-wrap');
    if (!wrap) return;
    this.treeSearch = new TreeSearch<LogLine, LogCtx>(wrap, {
      commands: LOG_COMMANDS,
      ctx: () => this.searchCtx,
      iterate: () => this.lines,
      valueForCommand: (cmd, item) => cmd.suggestValue?.(item),
      textMatch: logTextMatch,
      onChange: (q) => {
        this.query = q;
      },
    });
    // The shared column-resize chrome (blue wall + drag). The wall spans from the
    // header row down to the bottom, like the symbol tree.
    const panel = this.querySelector<HTMLElement>('.vg-wrapper');
    if (panel) {
      this.resizer = new ColumnResizer(panel, {
        highlightTop: () => {
          const h = this.querySelector('.vg-header');
          return h ? h.getBoundingClientRect().top - panel.getBoundingClientRect().top : 0;
        },
      });
    }
  }

  private setupVirtualizer(): void {
    if (this.virtualizer) return;
    this.virtualizer = new Virtualizer<HTMLElement, Element>({
      count: this.shown.length,
      getScrollElement: () => this.scrollEl(),
      estimateSize: () => ROW_HEIGHT_EST,
      overscan: OVERSCAN,
      observeElementOffset,
      observeElementRect,
      scrollToFn: elementScroll,
      scrollMargin: this.scrollMargin(),
      // Key by content so front-trimming (oldest dropped) keeps measured heights
      // attached to the right row instead of shifting by index.
      getItemKey: (i) => (this.shown[i] ? LogTree.key(this.shown[i]) : i),
      onChange: () => this.onVirtualizerChange(),
    });
    this.vCleanup = this.virtualizer._didMount();
    this.virtualizer._willUpdate();
  }

  /** Offset of `.vg-log-body` within the scroller — the virtualizer needs it to
   *  map page scroll → item positions (the body isn't the scroller; the page is). */
  private scrollMargin(): number {
    const se = this.scrollEl();
    const body = this.querySelector<HTMLElement>('.vg-log-body');
    if (!body) return 0;
    return body.getBoundingClientRect().top - se.getBoundingClientRect().top + se.scrollTop;
  }

  /** Virtualizer onChange (scroll / resize / measure): re-render when the visible
   *  index window moved OR a measurement changed the total layout size (a row
   *  measured taller than the estimate). The size check fixes overlapping rows
   *  on expand; it converges (stable measurements stop changing the total). */
  private onVirtualizerChange(): void {
    if (!this.virtualizer) return;
    const items = this.virtualizer.getVirtualItems();
    const start = items.length ? items[0].index : -1;
    const end = items.length ? items[items.length - 1].index : -1;
    const total = Math.round(this.virtualizer.getTotalSize());
    if (start !== this.renderedRange.start || end !== this.renderedRange.end || total !== this.renderedTotal) this.requestUpdate();
  }

  protected updated(): void {
    // Keep the virtualizer's count + scrollMargin in sync with the current list +
    // layout. Cheap; onChange only re-renders if the window actually moved (guard).
    if (this.virtualizer) {
      this.virtualizer.setOptions({
        ...this.virtualizer.options,
        count: this.shown.length,
        scrollMargin: this.scrollMargin(),
      });
      this.virtualizer._willUpdate();
    }
    if (this.scrollAfterUpdate) {
      this.scrollAfterUpdate = false;
      const se = this.scrollEl();
      se.scrollTop = se.scrollHeight;
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onKeydown);
    this.scrollParent?.removeEventListener('scroll', this.onScroll);
    this.vCleanup?.();
    this.vCleanup = undefined;
    this.virtualizer = undefined;
    this.scrollParent = null;
    this.treeSearch?.destroy();
    this.treeSearch = undefined;
    this.resizer?.destroy();
    this.resizer = undefined;
    this.teardown();
    this.wiredFor = '';
  }

  private teardown(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = 0;
    }
    this.flushScheduled = false;
    this.port?.postMessage({ kind: 'close' });
    this.port?.close();
    this.port = undefined;
    this.pending = [];
    this.lines = [];
    this.expandAllMode = false;
    this.toggled = new Set();
    this.selected = new Set();
    this.selectAllMode = false;
    this.following = false;
  }

  private scheduleFlush(): void {
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    // Fixed-cadence coalesce (see FLUSH_INTERVAL_MS): bounds render frequency by
    // wall-clock, not by stream rate, so a high-rate producer can't peg the page.
    this.flushTimer = window.setTimeout(() => this.flush(), FLUSH_INTERVAL_MS);
  }

  /** Are we (near) the bottom of the scroller (following the live tail)? */
  private isNearPageBottom(): boolean {
    const se = this.scrollEl();
    return se.scrollHeight - se.scrollTop - se.clientHeight < 48;
  }

  private flush(): void {
    this.flushScheduled = false;
    if (this.pending.length === 0 || !this.isConnected) {
      this.pending = [];
      return;
    }
    const incoming = this.pending;
    this.pending = [];
    let next = this.lines.length ? this.lines.concat(incoming) : incoming;
    // Dedup re-delivered lines (replay+live overlap shows the same event twice):
    // identical (time, title, body) collapse to one; distinct events survive.
    if (hasDupes(next)) {
      const seen = new Map<string, LogLine>();
      for (const l of next) seen.set(LogTree.key(l), l);
      next = [...seen.values()];
    }
    if (!isSorted(next)) next.sort((a, b) => a.tsMs - b.tsMs);
    // Trim ONLY the oldest, and ONLY when the user is following the live tail
    // (pinned to the bottom) — never while they're reading history, so the top
    // they're looking at is never deleted out from under them.
    const cap = this.following ? SOFT_CAP : HARD_CAP;
    if (next.length > cap) next = next.slice(next.length - cap);
    this.lines = next;
    // Stick to the bottom ONLY if the user is already following the tail.
    this.scrollAfterUpdate = this.following;
    // prune selection to current rows so the bulk count stays honest
    if (this.selected.size) {
      const live = new Set(next.map((l) => LogTree.key(l)));
      const pruned = new Set([...this.selected].filter((k) => live.has(k)));
      if (pruned.size !== this.selected.size) this.selected = pruned;
    }
  }

  private static sev(s: string): Sev {
    return sevOf(s);
  }

  /** Stable per-line key — full content (time+title+body) so re-deliveries of one
   *  event collapse, while distinct events keep their own row + selection. */
  private static key(l: LogLine): string {
    return `${l.tsMs}:${l.title}:${l.body ?? ''}`;
  }

  /** The Status-column chip: an explicit per-line status when the feed names
   *  one, else severity (routine info → no badge — right for fault logs). */
  private static badge(l: LogLine) {
    if (l.status) return { cls: `base-status-badge-${l.status.color}`, label: l.status.label };
    const s = LogTree.sev(l.severity);
    if (s === 'error') return { cls: 'base-status-badge-red', label: 'Error' };
    if (s === 'warn') return { cls: 'base-status-badge-yellow', label: 'Warning' };
    return null;
  }

  private fmtTime(tsMs: number): string {
    const d = new Date(tsMs);
    return `${fmtDate(d, { month: 'short', day: 'numeric' })} ${fmtTime(d, { hour12: false })}`;
  }

  /** The log's free-text + `/command` filter, via the SHARED tree-search engine
   *  (the same parser + matcher the symbol tree uses). */
  private filtered(): LogLine[] {
    const q = this.query.trim();
    if (!q) return this.lines;
    const parsed: ParsedSearch<LogLine, LogCtx> = parseSearchInput(q, LOG_COMMANDS);
    const ctx = this.searchCtx;
    return this.lines.filter((l) => matchesFilters(l, parsed, ctx, logTextMatch));
  }

  private get searchCtx(): LogCtx {
    return { fmtTime: (ms) => this.fmtTime(ms) };
  }

  // Arrow field so `this` is the component when used as a Lit @click listener.
  private clearSearch = (): void => {
    this.treeSearch?.setValue('');
    this.query = '';
  };

  private toggleRow(l: LogLine): void {
    const k = LogTree.key(l);
    const next = new Set(this.toggled);
    next.has(k) ? next.delete(k) : next.add(k);
    this.toggled = next;
  }

  private toggleSelect(l: LogLine): void {
    const k = LogTree.key(l);
    if (this.selectAllMode) {
      // deselecting one row out of "all" → leave the mode, keep the rest selected
      this.selectAllMode = false;
      this.selected = new Set(this.lines.map((x) => LogTree.key(x)).filter((key) => key !== k));
      return;
    }
    const next = new Set(this.selected);
    next.has(k) ? next.delete(k) : next.add(k);
    this.selected = next;
  }

  /** SELECT ALL is a MODE, not a snapshot of the current rows: every row stays
   *  selected, INCLUDING logs that stream in later. Re-press clears it. */
  private toggleSelectAll(): void {
    this.selectAllMode = !this.selectAllMode;
    this.selected = new Set();
  }

  /** Flip the global expand/collapse-all mode (clears per-row exceptions). */
  private toggleExpandAll(): void {
    this.expandAllMode = !this.expandAllMode;
    this.toggled = new Set();
  }

  private copySelected(): void {
    const rows = this.selectAllMode ? this.lines : this.lines.filter((l) => this.selected.has(LogTree.key(l)));
    // Each entry: "<timestamp>  <title>" + the indented body (sub-detail) when present.
    const text = rows
      .map((l) => {
        const head = `${this.fmtTime(l.tsMs)}  ${l.title}`;
        return l.body ? `${head}\n  ${l.body}` : head;
      })
      .join('\n\n');
    if (text) void navigator.clipboard?.writeText(text);
  }

  // ── Column resize (Time / Status) — via the SHARED ColumnResizer: same blue
  // wall + smooth imperative drag as the symbol tree. The log keeps only its own
  // px width math; the wall + drag plumbing live once in ColumnResizer. ──
  private hoverHandle(e: PointerEvent): void {
    const cell = (e.currentTarget as HTMLElement).closest('.vg-header-cell');
    const wrap = this.querySelector<HTMLElement>('.vg-wrapper');
    if (!cell || !wrap || !this.resizer) return;
    const x = cell.getBoundingClientRect().right - wrap.getBoundingClientRect().left - 1;
    this.resizer.setHover(x);
  }

  private leaveHandle(): void {
    this.resizer?.setHover(null);
  }

  private startResize(col: 'time' | 'status', e: PointerEvent): void {
    e.preventDefault();
    e.stopPropagation();
    const wrap = this.querySelector<HTMLElement>('.vg-wrapper');
    if (!wrap || !this.resizer) return;
    const wrapLeft = wrap.getBoundingClientRect().left;
    const startX = e.clientX;
    const start = col === 'time' ? this.colTime : this.colStatus;
    let time = this.colTime;
    let status = this.colStatus;
    this.resizer.beginDrag(
      startX,
      (delta) => {
        const w = Math.max(MIN_COL_W, start + delta);
        if (col === 'time') time = w;
        else status = w;
        wrap.style.setProperty('--vg-columns', `3.5% ${time}px ${status}px minmax(0, 1fr)`);
        return startX + delta - wrapLeft - 1; // the wall follows the cursor (centred)
      },
      () => {
        this.colTime = time;
        this.colStatus = status;
      },
    );
  }

  private renderRow(l: LogLine, it: VirtualItem) {
    const badge = LogTree.badge(l);
    const hasBody = !!l.body;
    // collapsed by default (expandAllMode=false); a per-row toggle flips it.
    const expanded = hasBody && this.expandAllMode !== this.toggled.has(LogTree.key(l));
    const collapsed = hasBody && !expanded;
    const isSel = this.selectAllMode || this.selected.has(LogTree.key(l));
    const rowCls = `vg-log-row${collapsed ? ' collapsed' : ''}${isSel ? ' selected' : ''}`;
    // Absolutely positioned at the virtualizer's offset; `ref` feeds the real
    // (variable) height back so the next row sits correctly. With `scrollMargin`,
    // @tanstack bakes the margin INTO item.start (item 0 sits at start=margin), so
    // subtract it to place rows relative to the body's own top.
    const margin = this.virtualizer?.options.scrollMargin ?? 0;
    return html`<div
      class=${rowCls}
      data-index=${it.index}
      style="position:absolute;left:0;top:0;width:100%;transform:translateY(${it.start - margin}px)"
      ${ref((el) => {
        if (el) this.virtualizer?.measureElement(el as HTMLElement);
      })}
    >
      <div class="vg-log-cell vg-col-select">
        <span class="vg-log-line">
          <input type="checkbox" class="vg-row-checkbox" .checked=${isSel} @change=${() => this.toggleSelect(l)} />
        </span>
      </div>
      <div class="vg-log-cell vg-log-time"><span class="vg-log-line">${this.fmtTime(l.tsMs)}</span></div>
      <div class="vg-log-cell vg-log-status">
        <span class="vg-log-line">
          ${
            badge
              ? html`<span class="base-status-badge ${badge.cls}"><span class="base-status-badge-label">${badge.label}</span></span>`
              : nothing
          }
        </span>
      </div>
      <div class="vg-log-cell vg-log-msgcell">
        <span class="vg-log-line">
          <span class="vg-chevron-wrap ${hasBody ? '' : 'vg-chev-hidden'}" @click=${hasBody ? () => this.toggleRow(l) : undefined}
            >${CHEVRON_SVG}</span
          ><span class="vg-log-title">${l.title}</span>
        </span>
        ${expanded ? html`<div class="vg-log-body-text">${l.body}</div>` : nothing}
      </div>
    </div>`;
  }

  render() {
    const shown = this.filtered();
    this.shown = shown; // backs the virtualizer (full list; only the window renders)
    const selCount = this.selectAllMode ? shown.length : shown.filter((l) => this.selected.has(LogTree.key(l))).length;
    const allSelected = this.selectAllMode || (shown.length > 0 && selCount === shown.length);
    const someSelected = !this.selectAllMode && selCount > 0 && selCount < shown.length;
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const cols = `--vg-columns: 3.5% ${this.colTime}px ${this.colStatus}px minmax(0, 1fr)`;

    return html`<div class="vg-wrapper vg-log" style=${cols}>
      <div class="vg-toolbar">
        <div class="vg-search-wrap">
          ${SEARCH_ICON}
          <div class="vg-search-highlight"></div>
          <input class="vg-search-input" type="text" placeholder=${t('grid.searchPlaceholder')} spellcheck="false" autocomplete="off" />
          ${this.query ? html`<button class="vg-search-clear" title="Clear" @click=${this.clearSearch}>${CLEAR_ICON}</button>` : nothing}
          <kbd class="vg-search-shortcut"><span>${isMac ? '⌘' : 'Ctrl'}</span>/</kbd>
        </div>
        <div class="vg-toolbar-actions">
          <!-- Copy sits to the LEFT of expand/collapse so expand/collapse never
               moves (it's the fixed right-most action — no layout shift). No
               clear-X: the select-all control toggles the whole selection. -->
          ${
            selCount > 0
              ? html`<span class="vg-selected-count">${selCount} selected</span>
                  <button class="vg-bulk-btn base-button base-button-blue base-button-sm" @click=${this.copySelected}>
                    ${COPY_ICON}<span class="vg-bulk-btn-label">Copy</span>
                  </button>`
              : nothing
          }
          ${
            // Expand-all governs the rows' BODY sections; a feed whose lines
            // have no bodies has nothing for it to do, and a button that does
            // nothing is furniture. Hidden, not disabled: disabled invites the
            // question this answers.
            shown.some((l) => !!l.body)
              ? html`<button
                  class="vg-toolbar-btn vg-btn-expand"
                  title=${this.expandAllMode ? t('grid.collapseAll') : t('grid.expandAll')}
                  @click=${() => this.toggleExpandAll()}
                >
                  ${wrapSvg(14, this.expandAllMode ? COLLAPSE_ICON : EXPAND_ICON)}
                </button>`
              : nothing
          }
        </div>
      </div>
      <div class="vg-header">
        <div class="vg-header-cell vg-col-select">
          <input
            type="checkbox"
            class="vg-select-all"
            title="Select all"
            .checked=${allSelected}
            .indeterminate=${someSelected}
            @change=${() => this.toggleSelectAll()}
          />
        </div>
        <div class="vg-header-cell">
          <span class="vg-header-label">Time</span>
          <div
            class="vg-resize-handle"
            @pointerenter=${this.hoverHandle}
            @pointerleave=${this.leaveHandle}
            @pointerdown=${(e: PointerEvent) => this.startResize('time', e)}
          ></div>
        </div>
        <div class="vg-header-cell">
          <span class="vg-header-label">Status</span>
          <div
            class="vg-resize-handle"
            @pointerenter=${this.hoverHandle}
            @pointerleave=${this.leaveHandle}
            @pointerdown=${(e: PointerEvent) => this.startResize('status', e)}
          ></div>
        </div>
        <div class="vg-header-cell"><span class="vg-header-label">Message</span></div>
      </div>
      ${
        shown.length === 0
          ? html`<div class="vg-empty-state">
              <div class="vg-empty-title">${this.lines.length === 0 ? this.emptyTitle : 'No matching entries'}</div>
              ${this.lines.length === 0 ? html`<div class="vg-empty-desc">${this.emptyDesc}</div>` : nothing}
            </div>`
          : this.renderBody(shown)
      }
    </div>`;
  }

  /** The virtualized log body: a `totalSize`-tall relative box holding ONLY the
   *  visible window of rows (absolutely positioned). DOM stays O(viewport) no
   *  matter how long `shown` is — this is the fix for the O(N)-DOM memory. */
  private renderBody(shown: LogLine[]) {
    const v = this.virtualizer;
    // Sync the virtualizer's count to the CURRENT list BEFORE reading its window, so
    // the FIRST render after data arrives reflects the new count immediately. (Count
    // was previously synced only in updated() — one cycle LATE — so the first paint
    // read a stale count of 0: getTotalSize()→0px body (page unscrollable) +
    // getVirtualItems()→[] (no rows), leaving the table BLANK until some later
    // scroll/resize observer happened to fire. On a busy page that observer is
    // delayed, so a fully-loaded backlog sat invisible for seconds — the "logs take
    // forever to pop up" bug. Syncing here makes getTotalSize()/getVirtualItems()
    // see the real count on this very render.)
    if (v && v.options.count !== shown.length) {
      v.setOptions({ ...v.options, count: shown.length, scrollMargin: this.scrollMargin() });
    }
    const margin = v?.options.scrollMargin ?? 0;
    // An estimated top window (uniform ROW_HEIGHT_EST), positioned EXACTLY like real
    // virtual items (@tanstack bakes scrollMargin INTO item.start, so we add it here
    // and renderRow subtracts it back). Used (a) before the virtualizer exists, and
    // (b) when it exists but hasn't measured the scroll rect yet — its ResizeObserver
    // fires a frame late, and the empty→body DOM swap means there was nothing to
    // measure until this very render, so getVirtualItems() can still be empty with a
    // correct count. Painting the estimate NOW means rows are never blank; the next
    // measure cycle (updated()→_willUpdate→onChange) swaps in the precise window.
    // Never render the whole list.
    const estimate = (): VirtualItem[] =>
      Array.from({ length: Math.min(shown.length, OVERSCAN * 3) }, (_, i) => ({
        index: i,
        key: LogTree.key(shown[i]),
        start: margin + i * ROW_HEIGHT_EST,
        end: margin + (i + 1) * ROW_HEIGHT_EST,
        size: ROW_HEIGHT_EST,
        lane: 0,
      }));
    let items: VirtualItem[] = v ? v.getVirtualItems() : estimate();
    if (items.length === 0 && shown.length > 0) items = estimate();
    const total = v && v.getTotalSize() > 0 ? v.getTotalSize() : shown.length * ROW_HEIGHT_EST;
    this.renderedTotal = Math.round(total);
    this.renderedRange = {
      start: items.length ? items[0].index : -1,
      end: items.length ? items[items.length - 1].index : -1,
    };
    return html`<div class="vg-log-body" style="position:relative;height:${total}px">
      ${repeat(
        items,
        (it) => it.key,
        (it) => {
          const l = shown[it.index];
          return l ? this.renderRow(l, it) : nothing;
        },
      )}
    </div>`;
  }
}

/** Cheap ascending-order check so we only sort a batch when a replay interleaved. */
function isSorted(rows: LogLine[]): boolean {
  for (let i = 1; i < rows.length; i++) {
    if (rows[i].tsMs < rows[i - 1].tsMs) return false;
  }
  return true;
}

/** Cheap duplicate check (same ms + title) so we only build the dedup map when needed. */
function hasDupes(rows: LogLine[]): boolean {
  const seen = new Set<string>();
  for (const r of rows) {
    const k = `${r.tsMs}:${r.title}`;
    if (seen.has(k)) return true;
    seen.add(k);
  }
  return false;
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-log-tree': LogTree;
  }
}
